import { theme } from "../theme";
import type { FrameworkId, OrgFramework } from "../context/FrameworksContext";

import { cbPalette } from "../lib/colourBlind";
export const FRAMEWORK_LABELS: Record<FrameworkId, string> = {
  iso27001:              "ISO 27001",
  cyber_essentials:      "Cyber Essentials",
  cyber_essentials_plus: "Cyber Essentials Plus",
  iso42001:              "ISO 42001",
  iso9001:               "ISO 9001",
  soc2:                  "SOC 2",
  pci_dss:               "PCI DSS v4.0",
  iasme_ca_sole_trader:  "IASME CA (Sole Trader)",
  iasme_ca_micro:        "IASME CA (Micro)",
  iasme_ca_small:        "IASME CA (Small)",
  iasme_ca_medium_large: "IASME CA (Medium/Large)",
  nist_csf:              "NIST CSF 2.0",
  gdpr:                  "GDPR",
  uk_gdpr:               "UK GDPR",
  iso45001:              "ISO 45001",
  iso14001:              "ISO 14001",
  iwa48:                 "IWA 48:2024",
  ncsc_caf:              "NCSC CAF",
};

export const FRAMEWORK_COLORS: Record<FrameworkId, string> = cbPalette({
  iso27001:              "#3b82f6",
  cyber_essentials:      "#10b981",
  cyber_essentials_plus: "#06b6d4",
  iso42001:              "#8b5cf6",
  iso9001:               "#f59e0b",
  soc2:                  "#ec4899",
  pci_dss:               "#dc2626",
  iasme_ca_sole_trader:  "#0ea5e9",
  iasme_ca_micro:        "#0284c7",
  iasme_ca_small:        "#0369a1",
  iasme_ca_medium_large: "#075985",
  nist_csf:              "#0d9488",
  gdpr:                  "#7c3aed",
  uk_gdpr:               "#6d28d9",
  iso45001:              "#16a34a",
  iso14001:              "#15803d",
  iwa48:                 "#065f46",
  ncsc_caf:              "#0f766e",
});

interface FrameworkFilterProps {
  frameworks: OrgFramework[];
  /** Set of framework IDs currently hidden. Empty = all visible. */
  hidden: Set<FrameworkId>;
  /** Toggle one framework's visibility. */
  onToggle: (id: FrameworkId) => void;
  /** Reset — show all frameworks. */
  onShowAll: () => void;
}

export function FrameworkFilter({ frameworks, hidden, onToggle, onShowAll }: FrameworkFilterProps) {
  if (frameworks.length === 0) return null;

  const allVisible = hidden.size === 0;

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
      <button
        onClick={onShowAll}
        style={{
          padding: "7px 14px",
          borderRadius: 3,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          border: `1px solid ${allVisible ? theme.color.accent : theme.color.borderStrong}`,
          background: allVisible ? theme.color.accentBg : "transparent",
          color: allVisible ? theme.color.accentText : theme.color.subtle,
        }}
      >
        All standards
      </button>
      {frameworks.map((f) => {
        const isVisible = !hidden.has(f.frameworkId);
        const color = FRAMEWORK_COLORS[f.frameworkId];
        return (
          <button
            key={f.frameworkId}
            onClick={() => onToggle(f.frameworkId)}
            title={isVisible ? `Hide ${FRAMEWORK_LABELS[f.frameworkId]}` : `Show ${FRAMEWORK_LABELS[f.frameworkId]}`}
            style={{
              padding: "7px 14px",
              borderRadius: 3,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              border: `1px solid ${isVisible ? color : theme.color.borderStrong}`,
              background: isVisible ? `${color}22` : "transparent",
              color: isVisible ? color : theme.color.subtle,
              opacity: isVisible ? 1 : 0.5,
            }}
          >
            {FRAMEWORK_LABELS[f.frameworkId]}
          </button>
        );
      })}
    </div>
  );
}
